/**
 * Advisory Error Codes
 *
 * The ONE error-code mapping layer for the Strategic Advisory feature.
 * Covers both Firestore error codes (`FirestoreError.code`) and the
 * backend SSE `error` event's provisional code vocabulary, mapping each to
 * `{ i18nKey, tone, retryable }`.
 *
 * Do NOT extend or import the two existing error-message utilities under
 * `src/utils/` — both are negative examples: they hardcode English copy
 * with no i18n key, no tone, and no retryable flag. This module is built
 * fresh, with zero dependency on either of them.
 */

/**
 * The two error tones: `info` is calm-informational (not the user's fault,
 * no retry offered), `failure` is failure-with-retry.
 */
export type AdvisoryErrorTone = 'info' | 'failure';

/**
 * One error-code mapping entry.
 */
export interface AdvisoryErrorMapping {
  i18nKey: string;
  tone: AdvisoryErrorTone;
  retryable: boolean;
}

/** Fallback code used when an incoming code has no known mapping. */
export const UNKNOWN_ADVISORY_ERROR_CODE = 'unknown';

/**
 * Client-side code the listener manager records when a document mapper
 * throws. Exported here so the manager imports this constant rather than
 * inventing a second string for the same condition.
 */
export const ADVISORY_MAPPER_ERROR_CODE = 'mapper-failed';

/**
 * Code used by useSupervisorTurn.ts for the SSE `error` event and for a
 * non-OK HTTP response from the supervisor endpoint. Named here so no call
 * site retypes the `'supervisor-error'` string literal — the literal now
 * exists in exactly one place, this module.
 */
export const ADVISORY_SUPERVISOR_ERROR_CODE = 'supervisor-error';

/** gmid/docintel refused the file type (400 on upload). Never retryable. */
export const ADVISORY_DOCUMENT_UNSUPPORTED_CODE = 'document-unsupported';

/** The file exceeded the upload ceiling (413). Never retryable. */
export const ADVISORY_DOCUMENT_TOO_LARGE_CODE = 'document-too-large';

/** The multipart upload itself failed for a reason that may not repeat. */
export const ADVISORY_DOCUMENT_UPLOAD_CODE = 'document-upload-failed';

/** Ingest was dispatched and docintel reported `failed`, or dispatch was refused. */
export const ADVISORY_DOCUMENT_INGEST_CODE = 'document-ingest-failed';

/** Ingest never reached `ready` inside the poll budget. Not the same as failed. */
export const ADVISORY_DOCUMENT_TIMEOUT_CODE = 'document-ingest-timeout';

/**
 * Every known error code this feature must render copy for, mapped to
 * `{ i18nKey, tone, retryable }`. `i18nKey` always resolves in
 * `public/locales/{en,de}/advisory.json`.
 */
export const ADVISORY_ERROR_MAPPINGS: Record<string, AdvisoryErrorMapping> = {
  // Firestore error codes (from FirestoreError.code).
  //
  // The `strategic_advisory/**` rules ARE deployed now (the live dev ruleset
  // covers the project document, `keyQuestions` and `tracking/**`), so this is no
  // longer the every-read outcome it was when this entry was written. It stays
  // `retryable: false` on its own merits: a denial means this caller may not read
  // this path, and retrying the same read with the same identity cannot change
  // that. A retry button here could never succeed.
  'permission-denied': {
    i18nKey: 'advisory:errors.permissionDenied',
    tone: 'info',
    retryable: false,
  },
  unauthenticated: { i18nKey: 'advisory:errors.unauthenticated', tone: 'info', retryable: false },
  'not-found': { i18nKey: 'advisory:errors.notFound', tone: 'info', retryable: false },
  unavailable: { i18nKey: 'advisory:errors.unavailable', tone: 'failure', retryable: true },
  'deadline-exceeded': {
    i18nKey: 'advisory:errors.deadlineExceeded',
    tone: 'failure',
    retryable: true,
  },
  'resource-exhausted': {
    i18nKey: 'advisory:errors.resourceExhausted',
    tone: 'failure',
    retryable: true,
  },
  cancelled: { i18nKey: 'advisory:errors.cancelled', tone: 'failure', retryable: true },
  internal: { i18nKey: 'advisory:errors.internal', tone: 'failure', retryable: true },

  // Client-side: a malformed document will fail identically on retry, so
  // this is the one `tone: 'failure'` entry that is deliberately
  // `retryable: false`.
  [ADVISORY_MAPPER_ERROR_CODE]: {
    i18nKey: 'advisory:errors.mapperFailed',
    tone: 'failure',
    retryable: false,
  },

  // Backend SSE `error` event. The backend documents the event but not
  // its code vocabulary. This single entry is a provisional stand-in
  // until the SSE error contract is published.
  [ADVISORY_SUPERVISOR_ERROR_CODE]: {
    i18nKey: 'advisory:errors.supervisorError',
    tone: 'failure',
    retryable: true,
  },

  // Document upload / ingest, for the advisory evidence step. Every one is a
  // FIXED key: docintel and gmid both return prose in their error bodies, and the
  // whole point of this table is that no matter what string arrives, only a
  // pre-authored key can be rendered (ASVS V7).
  //
  // The retryable split is the useful part. A rejected file type or an oversized
  // file will be rejected identically forever — offering a retry there is a lie.
  // A dispatch or ingest failure is usually transient (a cold Cloud Run Job, a
  // load-balancer hiccup), so those are retryable.
  [ADVISORY_DOCUMENT_UNSUPPORTED_CODE]: {
    i18nKey: 'advisory:errors.documentUnsupported',
    tone: 'info',
    retryable: false,
  },
  [ADVISORY_DOCUMENT_TOO_LARGE_CODE]: {
    i18nKey: 'advisory:errors.documentTooLarge',
    tone: 'info',
    retryable: false,
  },
  [ADVISORY_DOCUMENT_UPLOAD_CODE]: {
    i18nKey: 'advisory:errors.documentUploadFailed',
    tone: 'failure',
    retryable: true,
  },
  [ADVISORY_DOCUMENT_INGEST_CODE]: {
    i18nKey: 'advisory:errors.documentIngestFailed',
    tone: 'failure',
    retryable: true,
  },
  // Distinct from ingest-failed: docintel never said no, it simply had not
  // finished. A large scanned PDF goes through a VLM and genuinely takes minutes,
  // so the document may still become ready after this — which is why the copy
  // says the wait ended rather than that the document was rejected.
  [ADVISORY_DOCUMENT_TIMEOUT_CODE]: {
    i18nKey: 'advisory:errors.documentIngestTimeout',
    tone: 'failure',
    retryable: true,
  },

  // The documented placeholder entry: any code not recognised above, or a
  // null/undefined code, falls back to this.
  [UNKNOWN_ADVISORY_ERROR_CODE]: {
    i18nKey: 'advisory:errors.unknown',
    tone: 'failure',
    retryable: true,
  },
};

/**
 * Maps a raw error code to its `{ i18nKey, tone, retryable }` entry. Returns
 * the `unknown` entry for anything unrecognised, `null` or `undefined`.
 *
 * This function never echoes its input into the returned value — the
 * return is always one of the literal entries in `ADVISORY_ERROR_MAPPINGS`.
 * That is what keeps raw provider error text (e.g. `FirebaseError.message`)
 * out of rendered copy: no matter what string arrives, only a fixed,
 * pre-authored i18n key can result.
 */
export function mapAdvisoryErrorCode(code: string | null | undefined): AdvisoryErrorMapping {
  if (code && Object.prototype.hasOwnProperty.call(ADVISORY_ERROR_MAPPINGS, code)) {
    return ADVISORY_ERROR_MAPPINGS[code];
  }
  return ADVISORY_ERROR_MAPPINGS[UNKNOWN_ADVISORY_ERROR_CODE];
}
